import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
} from "@mui/material"
import { useState, useEffect, useCallback } from "react"
import { productService, ProductBasePopup } from "services/productService"
import debounce from "lodash/debounce"
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material"
import { toast } from "react-toastify"

interface DialogProductBaseProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: { productBaseId: number; batches: string[] }) => void
  isLoading?: boolean
  scheduledDate: string
}

const DialogNewProductBase = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  scheduledDate,
}: DialogProductBaseProps) => {
  const [formData, setFormData] = useState({
    productBaseId: 0,
    batches: [""],
  })
  const [productBases, setProductBases] = useState<ProductBasePopup[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductBasePopup | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      loadProductBases()
    }
  }, [open])

  const loadProductBases = async (searchTerm?: string) => {
    try {
      setSearchLoading(true)
      const data = await productService.getProductBases(searchTerm, 8500)
      setProductBases(data.results)
    } catch (error: any) {
      toast.error(error?.message ?? "Erro ao carregar produtos base. Tente novamente.")
    } finally {
      setSearchLoading(false)
    }
  }

  const debouncedLoadProductBases = useCallback(
    debounce((searchTerm?: string) => {
      loadProductBases(searchTerm)
    }, 600),
    [],
  )

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const validBatches = formData.batches.filter(batchId => batchId.trim() !== "")
      await onSubmit(formData)
      handleClose()
    } catch (error: any) {
      toast.error(error?.message ?? "Erro ao criar lotes. Tente novamente.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSearch = (event: any) => {
    const value = event.target.value
    debouncedLoadProductBases(value)
  }

  const handleProductSelect = (productBase: ProductBasePopup | null) => {
    setSelectedProduct(productBase)
    if (productBase) {
      setFormData({
        productBaseId: productBase.productBaseId,
        batches: [""],
      })
    }
  }

  const handleAddBatch = () => {
    if (formData.batches.length < 4) {
      setFormData(prev => ({
        ...prev,
        batches: [...prev.batches, ""],
      }))
    }
  }

  const handleRemoveBatch = (index: number) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.filter((_, i) => i !== index),
    }))
  }

  const handleBatchChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.map((batchId, i) => (i === index ? value : batchId)),
    }))
  }

  const handleClose = () => {
    setFormData({
      productBaseId: 0,
      batches: [""],
    })
    setSelectedProduct(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={() => !isLoading && handleClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Novo Produto Base</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Autocomplete
            options={productBases}
            getOptionLabel={option => option.name}
            value={selectedProduct}
            onChange={(_, newValue) => handleProductSelect(newValue)}
            noOptionsText="Produto não encontrado"
            loadingText="Buscando"
            renderInput={params => (
              <TextField
                {...params}
                label="Busque o Produto Base"
                onChange={handleSearch}
                disabled={isLoading || isSubmitting}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={searchLoading}
            sx={{ mb: 2 }}
          />

          {selectedProduct && (
            <>
              <TextField
                margin="dense"
                label="Data de Entrega"
                type="text"
                fullWidth
                value={scheduledDate.split("-").reverse().join("/")}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                label="Lote"
                type="text"
                fullWidth
                value={formData.batches[0]}
                onChange={e => handleBatchChange(0, e.target.value)}
                required
                disabled={isLoading || isSubmitting}
                sx={{ mb: 1 }}
              />

              {formData.batches.slice(1).map((batchId, index) => (
                <div key={index + 1} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    margin="dense"
                    label={`Lote ${index + 2}`}
                    type="text"
                    fullWidth
                    value={batchId}
                    onChange={e => handleBatchChange(index + 1, e.target.value)}
                    required
                    disabled={isLoading || isSubmitting}
                    sx={{ mb: 1 }}
                  />
                  <IconButton
                    onClick={() => handleRemoveBatch(index + 1)}
                    color="error"
                    disabled={isLoading || isSubmitting}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}

              {formData.batches.length < 4 && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddBatch}
                  disabled={isLoading || isSubmitting}
                  sx={{ mt: 1 }}
                  color="secondary"
                >
                  Adicionar Lote
                </Button>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isLoading || isSubmitting}
            style={{ color: "#000" }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={isLoading || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? "Criando..." : "Criar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default DialogNewProductBase
